import {assertAndContinue} from 'Asserts';

export enum NameResolutionType {
  NOT_FOUND = 'NOT_FOUND',
  FOUND = 'FOUND',
  FOUND_MULTIPLE = 'FOUND_MULTIPLE',
}

export type NameResolution<T> =
  | {type: NameResolutionType.NOT_FOUND}
  | {type: NameResolutionType.FOUND; value: T}
  | {type: NameResolutionType.FOUND_MULTIPLE};

export type NameResolver<T> = (name: string) => NameResolution<T>;

/**
 * Return a function that can be called repeatedly to resolve by name in the given collection.
 *
 * The resolution only matches if there is a unique case-insensitive match.
 */
export function getNameResolver<T>(collection: Array<T>, getName: (t: T) => string): NameResolver<T> {
  // Implement groupBy using a map rather than an object to avoid trouble if the name __proto__ exists.
  const valuesByLowerName = new Map<string, Array<T>>();
  for (const value of collection) {
    const name = getName(value).toLowerCase();
    const valuesForName = valuesByLowerName.get(name) ?? [];
    valuesForName.push(value);
    valuesByLowerName.set(name, valuesForName);
  }

  return (name): NameResolution<T> => {
    const matches = valuesByLowerName.get(name.toLowerCase());
    if (matches == null) {
      return {type: NameResolutionType.NOT_FOUND};
    } else if (!assertAndContinue(matches.length >= 1, 'Found unexpected empty matches array.')) {
      return {type: NameResolutionType.NOT_FOUND};
    } else if (matches.length === 1) {
      return {type: NameResolutionType.FOUND, value: matches[0]};
    } else {
      return {type: NameResolutionType.FOUND_MULTIPLE};
    }
  };
}
